import { useQueryClient } from '@tanstack/react-query'
import { isBefore } from 'date-fns'
import { useRef, KeyboardEvent } from 'react'

import { roomQueries } from '@/entities/room'
import { formatDateRange, StringDateRange } from '@/shared/lib'
import { type RangeDatePickerRef } from '@/shared/ui'

import { useDateRange } from '../model/date-range'
import { useKeyword } from '../model/keyword'
import {
  convertToSearchHistory,
  type KeywordSearchHistory,
} from '../model/keyword-search-history-store/history'
import { useKeywordSearchHistoryStore } from '../model/keyword-search-history-store/store'

type Props = {
  search: (params: {
    keyword: string
    startDate?: string
    endDate?: string
  }) => void
}

export const useKeywordSearch = ({ search }: Props) => {
  const queryClient = useQueryClient()

  const periodPickerRef = useRef<RangeDatePickerRef>({
    focus: () => {},
  })

  const historyActions = useKeywordSearchHistoryStore(state => state.actions)

  const { dateRange, updateDateRange } = useDateRange()
  const { keyword, updateKeyword } = useKeyword()

  const moveFocusToPeriodPicker = () => {
    periodPickerRef.current.focus()
  }

  const handleDateRangeChange = (dateRange?: StringDateRange) => {
    updateDateRange(dateRange)
  }

  const handleRecommendKeywordClick = (keyword: string) => {
    updateKeyword(keyword)
    moveFocusToPeriodPicker()
  }

  const handleRecentHistoryClick = (history: KeywordSearchHistory) => {
    const { keyword, startDate, endDate } = history

    const today = new Date().setHours(0, 0, 0, 0)
    const isBeforeToday = isBefore(startDate, today)

    if (isBeforeToday) {
      updateKeyword(keyword)
      updateDateRange(undefined)

      moveFocusToPeriodPicker()

      return
    }

    historyActions.addSearchHistory(history)

    search({
      keyword,
      startDate,
      endDate,
    })

    updateKeyword(keyword)
    updateDateRange({
      from: startDate,
      to: endDate,
    })
  }

  const handleInputEnterKeyDown = () => {
    moveFocusToPeriodPicker()
  }

  const handleInputBlur = async () => {
    await queryClient.invalidateQueries({
      queryKey: roomQueries.recentViewedRooms().queryKey,
      refetchType: 'none',
    })
  }

  const handlePeriodPickerKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleSubmit = () => {
    const trimmedKeyword = keyword.trim()

    if (trimmedKeyword || dateRange) {
      historyActions.addSearchHistory(
        convertToSearchHistory(trimmedKeyword, dateRange),
      )
    }

    search({
      keyword: trimmedKeyword,
      startDate: dateRange?.from,
      endDate: dateRange?.to,
    })
  }

  return {
    keywordInputProps: {
      value: keyword,
      onChange: updateKeyword,
      onBlur: handleInputBlur,
      onRecentHistoryClick: handleRecentHistoryClick,
      onRecommendKeywordClick: handleRecommendKeywordClick,
      onEnterKeyDown: handleInputEnterKeyDown,
    },
    periodPickerProps: {
      value: formatDateRange({ dateRange }),
      onChange: handleDateRangeChange,
      onEnterKeyDown: handlePeriodPickerKeyDown,
      ref: periodPickerRef,
    },
    handleSubmit,
  }
}
